import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { getRef } from '../utils/ref';
import { StyledBaseIcon } from './Icons/Base.icon';

export interface IconButtonProps
  extends React.AllHTMLAttributes<HTMLDivElement>,
    getRef<any> {
  mode?: 'default' | 'transparent';
  badge?: number;
  badgeAppearance?: 'red' | 'blue' | 'gray';
  link?: string;
  width?: string;
  height?: string;
}

const StyledIconButtonLink = styled.div`
  & > a {
    display: flex;
  }
`;
const StyledIconButtonBadgeGray = css`
  background: ${({ theme }) => theme.colors.BORDER};
  color: ${({ theme }) => theme.colors.TITLE};
`;
const StyledIconButtonBadgeBlue = css`
  background: ${({ theme }) => theme.colors.BLUE.B400};
  color: white;
`;
const StyledIconButtonBadgeRed = css`
  background: ${({ theme }) => theme.colors.RED.R400};
  color: white;
`;
const StyledIconButtonBadge = styled.div<{
  appearance: string;
}>`
  position: absolute;
  right: -7px;
  top: -8px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 11px;
  height: 11px;
  padding: 3px;
  border-radius: 50%;
  font-size: 10px;
  line-height: 9px;
  font-weight: 600;

  ${props => props.appearance === 'red' && StyledIconButtonBadgeRed};
  ${props => props.appearance === 'blue' && StyledIconButtonBadgeBlue};
  ${props => props.appearance === 'gray' && StyledIconButtonBadgeGray};
`;
export const StyledIconButtonDefault = css`
  background: ${({ theme }) => theme.colors.BG};
`;
const StyledIconButtonEnabled = css`
  cursor: pointer;

  ${StyledBaseIcon} {
    transition: color 0.15s;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.CARD};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);

    & > ${StyledBaseIcon} {
      color: ${({ theme }) => theme.colors.BLUE.B400};
    }
  }
`;
export const StyledIconButton = styled.div<{
  disabled: boolean;
  mode: string;
  width?: string;
  height?: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  position: relative;

  ${props => !props.disabled && StyledIconButtonEnabled};
  ${props => props.disabled && `opacity: 0.7;pointer-events: none;`};
  ${props => props.width && `width: ${props.width}`};
  ${props => props.height && `height: ${props.height}`};

  ${props => props.mode === 'default' && StyledIconButtonDefault};
`;
const IconButtonComponent = ({
  mode = 'default',
  badge = 0,
  badgeAppearance = 'red',
  children,
  onClick,
  getRef,
  link,
  width,
  height,
  disabled = false,
}: IconButtonProps) => {
  const renderContent = (
    <StyledIconButton
      disabled={disabled}
      ref={getRef}
      onClick={onClick}
      mode={mode}
      width={width}
      height={height}
    >
      {badge > 0 && (
        <StyledIconButtonBadge appearance={badgeAppearance}>
          {badge}
        </StyledIconButtonBadge>
      )}
      {children}
    </StyledIconButton>
  );

  return link ? (
    <StyledIconButtonLink>
      <Link to={link} target="_blank">
        {renderContent}
      </Link>
    </StyledIconButtonLink>
  ) : (
    renderContent
  );
};

export default IconButtonComponent;
